/**
 * Shared copy for order activity timeline (portal + app).
 * Keep presentation-only; no I/O.
 */

export type OrderEventType =
  | 'order_created'
  | 'payment_paid'
  | 'payment_refunded'
  | 'status_confirmed'
  | 'status_preparing'
  | 'status_ready'
  | 'status_dispatched'
  | 'status_delivered'
  | 'status_cancelled'
  | 'change_proposed'
  | 'change_approved'
  | 'change_rejected'
  | 'change_expired';

export type OrderEventActorRole = 'customer' | 'business' | 'system';

export type OrderActivityEvent = {
  id: string;
  order_id: string;
  event_type: OrderEventType;
  payload: Record<string, unknown>;
  actor_role: OrderEventActorRole;
  actor_id: string | null;
  created_at: string;
};

export type OrderActivityLine = {
  id: string;
  at: string;
  title: string;
  detail: string | null;
};

function str(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function num(payload: Record<string, unknown>, key: string): number | null {
  const v = payload[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function changeVerb(changeType: string | null): string {
  switch (changeType) {
    case 'replace':
      return 'reemplazo';
    case 'reduce_quantity':
      return 'menos unidades';
    case 'remove':
      return 'quitar producto';
    default:
      return 'cambio';
  }
}

function changeDetail(payload: Record<string, unknown>, perspective: 'customer' | 'business'): string | null {
  const changeType = str(payload, 'change_type');
  const product = str(payload, 'product_name');
  const proposed = str(payload, 'proposed_product_name');
  const proposedQty = num(payload, 'proposed_quantity');
  const originalQty = num(payload, 'original_quantity');
  const reason = str(payload, 'reason');

  const parts: string[] = [];
  if (changeType === 'replace' && product && proposed) {
    parts.push(`${product} → ${proposed}`);
  } else if (changeType === 'reduce_quantity' && product && proposedQty != null && originalQty != null) {
    parts.push(`${product}: ${originalQty} → ${proposedQty} u.`);
  } else if (changeType === 'remove' && product) {
    parts.push(product);
  } else if (product) {
    parts.push(product);
  }
  if (reason) parts.push(`Motivo: ${reason}`);
  if (perspective === 'business' && changeType === 'replace' && proposedQty != null) {
    parts.push(`Cant. ${proposedQty}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * @param perspective customer = buyer copy ("Aceptaste…"); business = seller copy ("El cliente aceptó…")
 */
export function formatOrderActivityEvent(
  event: OrderActivityEvent,
  perspective: 'customer' | 'business',
): OrderActivityLine {
  const p = event.payload ?? {};
  const changeType = str(p, 'change_type');
  const verb = changeVerb(changeType);

  let title: string;
  switch (event.event_type) {
    case 'order_created':
      title = 'Pedido realizado';
      break;
    case 'payment_paid':
      title = str(p, 'payment_provider') === 'mercadopago'
        ? 'Pago acreditado (Mercado Pago)'
        : 'Pago acreditado';
      break;
    case 'payment_refunded':
      title = 'Reembolso procesado';
      break;
    case 'status_confirmed':
      title = perspective === 'customer' ? 'El kiosco confirmó el pedido' : 'Pedido confirmado';
      break;
    case 'status_preparing':
      title = perspective === 'customer' ? 'El kiosco está preparando el pedido' : 'En preparación';
      break;
    case 'status_ready':
      title = perspective === 'customer' ? 'Pedido listo para retirar' : 'Marcado listo para retirar';
      break;
    case 'status_dispatched':
      title =
        perspective === 'customer'
          ? str(p, 'source') === 'platform_rider'
            ? 'Rider en camino'
            : 'Tu pedido va en camino'
          : 'Pedido despachado';
      break;
    case 'status_delivered':
      title = 'Pedido entregado';
      break;
    case 'status_cancelled':
      title = 'Pedido cancelado';
      break;
    case 'change_proposed':
      title =
        perspective === 'customer'
          ? `El kiosco propone: ${verb}`
          : `Propuesta enviada: ${verb}`;
      break;
    case 'change_approved':
      title =
        perspective === 'customer'
          ? `Aceptaste el ${verb}`
          : `El cliente aceptó el ${verb}`;
      break;
    case 'change_rejected':
      title =
        perspective === 'customer'
          ? `Rechazaste el ${verb}`
          : `El cliente rechazó el ${verb}`;
      break;
    case 'change_expired':
      title =
        perspective === 'customer'
          ? 'La propuesta venció — el pedido fue cancelado'
          : 'El cliente no respondió — pedido cancelado';
      break;
    default:
      title = event.event_type;
  }

  const detail =
    event.event_type.startsWith('change_')
      ? changeDetail(p, perspective)
      : null;

  return {
    id: event.id,
    at: event.created_at,
    title,
    detail,
  };
}

export function formatOrderActivityTime(iso: string): string {
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
